import { useMemo, useState } from 'react';
import { useApp } from '../store';
import { PROJECT_ROLES, ROLE_BLURB, ROLE_LABEL, type ProjectRole, type Role } from '../permissions';
import { parseAddresses, type Profiles } from '../people';
import { confirmDialog } from '../confirm';
import VendorTag from './VendorTag';
import Icon from './Icon';
import Info from './Info';

/**
 * ADDING SOMEBODY, PROPERLY.
 *
 * WHAT THIS REPLACES. One text box, one dropdown, one button — an address and a
 * role and nothing else. A person arrived with no name, no job title, and the
 * same role on every board at once, and whoever added them then had to go and
 * fix all of that afterwards on a different screen. Vinnie, 14 August: *"I
 * don't want a user add just by entering an email address... a real user
 * screen."*
 *
 * THE ORDER IS THE ORDER SOMEBODY THINKS IN. Who they are, then what they can
 * do, then where they can do it. Not the order the database wants, which would
 * put the address first because it is the key.
 *
 * BOARD ACCESS DEFAULTS TO THE WORKSPACE ROLE, so the ordinary case is still
 * three fields and a button. A screen that demanded a decision per board would
 * be slower than the box it replaces, which would make it a worse screen with
 * more fields on it.
 *
 * THE PASTE BOX DOES NOT DIE. Adding a development team of twenty is a real
 * thing, and a form that asked for twenty first names would make that worse
 * rather than better. It lives behind one link, takes a list, and applies one
 * role to all of them — which is exactly what the old box did.
 */
export default function AddPerson({ onDone }: { onDone: () => void }) {
  const addPerson = useApp((s) => s.addPerson);
  const projects = useApp((s) => s.projects);
  const access = useApp((s) => s.access);
  const lastInviteNote = useApp((s) => s.lastInviteNote);

  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [email, setEmail] = useState('');
  const [title, setTitle] = useState('');
  const [role, setRole] = useState<ProjectRole>('member');
  const [admin, setAdmin] = useState(false);
  const [temp, setTemp] = useState(true);
  /**
   * WHO THEY ARE — Internal (the default, everything as before) or an External /
   * Vendor (Feature B). Choosing Vendor sets the company role to vendor AT
   * CREATION, replaces the internal role ladder with the one Vendor access note,
   * locks the admin toggle, and grants every ticked board as `vendor` — so there
   * is never a window where they read as a full internal member.
   */
  const [ident, setIdent] = useState<'internal' | 'vendor'>('internal');
  const isVendor = ident === 'vendor';
  /** board id → the role they hold there. Absent means no access at all. For a
      vendor every ticked board is held as `vendor`; for an internal person it is
      the workspace role chosen above. */
  const [boards, setBoards] = useState<Record<string, ProjectRole>>({});

  const [bulk, setBulk] = useState(false);
  const [list, setList] = useState('');

  const [busy, setBusy] = useState(false);
  const [trouble, setTrouble] = useState('');
  /** The password, shown once. Never read back from anywhere. */
  const [handed, setHanded] = useState('');
  const [copied, setCopied] = useState(false);

  const address = email.trim().toLowerCase();
  const already = access.emails.includes(address);
  const looksLikeAddress = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address);

  /*
   * SAID BEFORE THEY PRESS, not after. A rule that only appears on failure is a
   * rule somebody meets by guessing — the same reasoning as the password gate.
   */
  const problem = !address
    ? ''
    : !looksLikeAddress
      ? 'That does not look like an email address.'
      : already
        ? 'Somebody with that address is already in this workspace.'
        : '';
  const ready = looksLikeAddress && !already && !busy;

  const parsed = useMemo(() => parseAddresses(list, access.emails), [list, access.emails]);

  const toggleBoard = (id: string) =>
    setBoards((was) => {
      const next = { ...was };
      if (id in next) delete next[id];
      // A vendor joins every ticked board as `vendor`; an internal person starts
      // on the workspace role chosen above.
      else next[id] = isVendor ? 'vendor' : role;
      return next;
    });

  /* Switching identity re-maps every already-ticked board to the new tier, so the
     two can never disagree — a board ticked as a member and then switched to Vendor
     is granted as a vendor, not left as a member. */
  const chooseIdent = (next: 'internal' | 'vendor') => {
    setIdent(next);
    setBoards((was) => {
      const out: Record<string, ProjectRole> = {};
      for (const id of Object.keys(was)) out[id] = next === 'vendor' ? 'vendor' : role;
      return out;
    });
  };

  /* The Vendor name-tag travels with the person from the moment of creation. The
     profiles watcher has not echoed the new row yet when the handoff shows, so the
     tag is rendered from what we just chose — the person IS a vendor — through the
     one shared atom, never a bespoke chip (R28). */
  const vendorProfiles: Profiles = { [address]: { companyRole: 'vendor' } };

  const save = async () => {
    if (!ready) return;

    /*
     * THE ONE CONSCIOUS EXTERNAL GRANT (R24). Adding a vendor writes the company
     * role AND every board grant as `vendor` together — the person is never a full
     * internal member for even a moment — so it is confirmed once. The copy mirrors
     * the per-board vendor confirm in PersonAccess, so the two reads cannot drift.
     */
    if (isVendor) {
      const picked = projects.filter((p) => p.id in boards);
      const naming =
        picked.length === 0
          ? 'no boards yet'
          : picked.map((p) => `${p.key} · ${p.name}`).join(', ');
      const ok = await confirmDialog({
        title: `Add ${[first, last].filter(Boolean).join(' ') || address} as an external Vendor?`,
        body: (
          <>
            They join as an <b>External / Vendor</b> — an outside supplier. On{' '}
            <b>{naming}</b> they can see and update cards and post updates, but not its settings,
            finances, sign-offs, decisions, or any other board. They can never be a workspace admin.
          </>
        ),
        confirmLabel: 'Add as Vendor',
        tone: 'caution',
      });
      if (!ok) return;
    }

    setBusy(true);
    setTrouble('');
    void addPerson(
      address,
      // A vendor is added with the top-level role `vendor` and never as an admin;
      // an internal person keeps the ladder-or-admin choice exactly as before.
      (isVendor ? 'vendor' : admin ? 'admin' : role) as Role,
      { first: first.trim(), last: last.trim() },
      {
        title: title.trim() || undefined,
        boards: Object.entries(boards).map(([id, r]) => ({ id, role: r })),
        tempPassword: temp,
        ...(isVendor ? { companyRole: 'vendor' as const } : {}),
      }
    ).then(
      (password) => {
        setBusy(false);
        /*
         * A password stops the screen; without one there is nothing more to
         * see and closing is the right answer. Somebody holding a password
         * that exists nowhere else must not have it taken off the screen by a
         * redirect they did not ask for.
         */
        if (password) setHanded(password);
        else onDone();
      },
      (e: unknown) => {
        setBusy(false);
        setTrouble(e instanceof Error ? e.message : String(e));
      }
    );
  };

  const saveList = () => {
    const ready = parsed.ready;
    if (!ready.length) return;
    setBusy(true);
    setTrouble('');
    void (async () => {
      try {
        // One at a time: the function checks the caller's standing per request,
        // and a batch that half-applied would be worse than a slower loop.
        for (const one of ready) await addPerson(one, role as Role);
        onDone();
      } catch (e) {
        setTrouble(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
      }
    })();
  };

  /* ── The password, shown once ──────────────────────────────────────────── */

  if (handed) {
    return (
      <div className="add-person">
        <button className="btn btn-small add-back" onClick={onDone}>
          ← People and access
        </button>
        <h2 className="add-handed-h">
          {[first, last].filter(Boolean).join(' ') || address}
          {isVendor && <VendorTag who={address} profiles={vendorProfiles} />}{' '}
          <span className="add-handed-was">has been added</span>
        </h2>

        <div className="card add-card handed">
          <p className="handed-label">Their temporary password</p>
          <p className="handed-value">{handed}</p>
          <div className="handed-actions">
            <button
              className="btn"
              onClick={() => {
                void navigator.clipboard?.writeText(handed).then(
                  () => setCopied(true),
                  () => setCopied(false)
                );
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          {/*
            SAID PLAINLY, BECAUSE IT IS TRUE AND BECAUSE IT IS THE ONLY WARNING
            THERE WILL BE. Nothing stores this. Leaving the screen loses it, and
            the only way back is to issue another one.
          */}
          <p className="handed-warn">
            This is the only time it is shown. It is not saved anywhere and cannot be looked up —
            if you lose it, issue a new one.
          </p>
          <p className="hint">
            Give it to them however you normally reach them. They will be asked to choose their own
            password before they can open anything, so this gets them through the door and no
            further.
          </p>
          {lastInviteNote && <p className="hint">{lastInviteNote}</p>}
        </div>

        <div className="add-actions">
          <button className="btn btn-primary" onClick={onDone}>
            Done
          </button>
        </div>
      </div>
    );
  }

  /* ── Adding several at once ────────────────────────────────────────────── */

  if (bulk) {
    return (
      <div className="add-person">
        <button className="btn btn-small add-back" onClick={() => setBulk(false)}>
          ← Add one person
        </button>
        <h2>Add several people</h2>
        <p className="hint">
          Paste the addresses — commas, spaces or new lines all work. They all get the same role,
          and none of them get a name or a job title until they set their own.
        </p>

        <div className="card add-card">
          <label className="add-field">
            Their addresses
            <textarea
              className="input"
              rows={6}
              value={list}
              onChange={(ev) => setList(ev.target.value)}
              placeholder={'one@example.com\ntwo@example.com'}
            />
          </label>
          <label className="add-field">
            Role
            <select value={role} onChange={(ev) => setRole(ev.target.value as ProjectRole)}>
              {PROJECT_ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r]}
                </option>
              ))}
            </select>
          </label>
          {list.trim() && <p className="hint">{describe(parsed)}</p>}
        </div>

        {trouble && <p className="add-trouble">{trouble}</p>}

        <div className="add-actions">
          <button className="btn btn-primary" disabled={busy || !parsed.ready.length} onClick={saveList}>
            {busy ? 'Adding…' : `Add ${parsed.ready.length || ''}`.trim()}
          </button>
          <button className="btn" onClick={onDone}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  /* ── One person, properly ──────────────────────────────────────────────── */

  return (
    <div className="add-person">
      <button className="btn btn-small add-back" onClick={onDone}>
        ← People and access
      </button>
      <h2>Add someone</h2>
      <p className="hint">
        They will be asked to choose their own password before they can open anything, whichever
        way they get in.
      </p>

      <div className="card add-card">
        <p className="add-legend">Who they are</p>
        <div className="add-pair">
          <label className="add-field">
            First name
            <input className="input" value={first} onChange={(ev) => setFirst(ev.target.value)} autoComplete="off" />
          </label>
          <label className="add-field">
            Last name
            <input className="input" value={last} onChange={(ev) => setLast(ev.target.value)} autoComplete="off" />
          </label>
        </div>
        <label className="add-field">
          <span className="stack-lbl">Email address <span className="req">*</span></span>
          <input
            className="input"
            type="email"
            value={email}
            onChange={(ev) => {
              setEmail(ev.target.value);
              setTrouble('');
            }}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            placeholder="their@email.com"
          />
        </label>
        {problem && <p className="add-problem">{problem}</p>}
        <label className="add-field">
          <Info
            className="add-lbl"
            title="What they do here"
            what="Shown next to their name, so people can place them — a role, what they are advising on, or how long they are here."
          >
            What they do here
          </Info>
          <input
            className="input"
            value={title}
            onChange={(ev) => setTitle(ev.target.value)}
            placeholder="Designer · Advising on the API · Contractor until March"
          />
        </label>

        {/* WHO THEY ARE — Internal (default) or an External / Vendor. The same
            .add-role radio-card the role ladder uses (R3), laid out as a pair.
            Internal selected changes nothing below; Vendor adapts the form. */}
        <div className="add-field">
          This person is
          <div className="add-ident">
            <button
              type="button"
              className={`add-role${!isVendor ? ' on' : ''}`}
              aria-pressed={!isVendor}
              onClick={() => chooseIdent('internal')}
            >
              <span className="add-radio" />
              <span>
                <span className="ident-top">
                  <b>Internal</b>
                </span>
                <em>Part of your organisation. Gets an internal role across the workspace.</em>
              </span>
            </button>
            <button
              type="button"
              className={`add-role${isVendor ? ' on' : ''}`}
              aria-pressed={isVendor}
              onClick={() => chooseIdent('vendor')}
            >
              <span className="add-radio" />
              <span>
                <span className="ident-top">
                  <span className="ident-glyph">
                    <Icon name="building" size={15} />
                  </span>
                  <b>External / Vendor</b>
                </span>
                <em>An outside supplier, confined to the boards you tick.</em>
              </span>
            </button>
          </div>
        </div>
      </div>

      <div className="card add-card">
        <p className="add-legend">
          What they can do{' '}
          <span className="hint">
            {isVendor
              ? '— as an external vendor, on the boards you tick'
              : '— across the workspace, unless a board says otherwise'}
          </span>
        </p>

        {isVendor ? (
          /* The internal ladder is replaced by the one Vendor access note — a
             vendor is never offered an internal role (B-03). A plain bordered
             note, no coloured side rail (R12); the copy mirrors ROLE_BLURB.vendor
             and the PersonAccess confirm so the reads cannot drift. */
          <div className="add-vtier">
            <span className="vt-ic">
              <Icon name="building" size={20} />
            </span>
            <span className="vt-body">
              <b>Vendor access</b>
              <em>
                An outside supplier working on the boards you tick: they see and update those boards’
                cards, raise issues and post updates. They cannot reach the plan, the budget,
                sign-offs, decisions, any board’s settings, or any board you don’t tick.
              </em>
            </span>
          </div>
        ) : (
          <div className="add-roles">
            {PROJECT_ROLES.map((r) => (
              <button
                key={r}
                type="button"
                className={`add-role${role === r && !admin ? ' on' : ''}`}
                aria-pressed={role === r && !admin}
                disabled={admin}
                onClick={() => setRole(r)}
              >
                <span className="add-radio" />
                <span>
                  <b>{ROLE_LABEL[r]}</b>
                  <em>{ROLE_BLURB[r]}</em>
                </span>
              </button>
            ))}
          </div>
        )}

        {isVendor ? (
          /* Shown visible-but-locked, not hidden, so the admin reads the rule
             rather than wondering where the toggle went. */
          <label className="add-toggle is-locked">
            <input type="checkbox" disabled checked={false} readOnly />
            <span>
              <b>Also a workspace admin</b>
              <em className="lockline">
                <Icon name="lock" size={13} /> A vendor can never be a workspace admin.
              </em>
            </span>
          </label>
        ) : (
          <label className="add-toggle">
            <input type="checkbox" checked={admin} onChange={(ev) => setAdmin(ev.target.checked)} />
            <span>
              <b>Also a workspace admin</b>
              <em>{ROLE_BLURB.admin} Give this to as few people as possible.</em>
            </span>
          </label>
        )}
      </div>

      {projects.length > 0 && (
        <div className="card add-card">
          <p className="add-legend">
            Which boards{' '}
            <span className="hint">
              — {Object.keys(boards).length} of {projects.length} selected
              {isVendor ? ' · each is granted as Vendor' : ''}
            </span>
          </p>
          <div className="add-boards">
            {projects.map((p) => {
              const on = p.id in boards;
              return (
                <div key={p.id} className={`add-board${on ? ' on' : ''}`}>
                  <label className="add-board-pick">
                    <input type="checkbox" checked={on} onChange={() => toggleBoard(p.id)} />
                    <span className="add-bkey">{p.key}</span>
                    <span className="add-bname">{p.name}</span>
                  </label>
                  {on ? (
                    isVendor ? (
                      /* A vendor board is granted as `vendor`, fixed — no role
                         dropdown, matching how PersonAccess grants it. The shared
                         VendorTag atom marks it. */
                      <VendorTag who={address} profiles={vendorProfiles} />
                    ) : (
                      <select
                        value={boards[p.id]}
                        onChange={(ev) =>
                          setBoards((was) => ({ ...was, [p.id]: ev.target.value as ProjectRole }))
                        }
                      >
                        {PROJECT_ROLES.map((r) => (
                          <option key={r} value={r}>
                            {ROLE_LABEL[r]}
                          </option>
                        ))}
                      </select>
                    )
                  ) : (
                    <span className="add-bnone">No access</span>
                  )}
                </div>
              );
            })}
          </div>
          <p className="hint">
            {isVendor
              ? 'A board left unticked is one they cannot open — they never see it. Every board you tick, they join as an external vendor.'
              : 'A board left unticked is one they cannot open at all. Ticked, they start on the workspace role above.'}
          </p>
          {isVendor && Object.keys(boards).length === 0 && (
            <p className="add-problem">
              No boards ticked yet — you can still add them as a Vendor and give them a board later.
            </p>
          )}
        </div>
      )}

      <div className="card add-card">
        <p className="add-legend">How they get in</p>
        <label className="add-toggle">
          <input type="checkbox" checked={temp} onChange={(ev) => setTemp(ev.target.checked)} />
          <span>
            <b>Give me a temporary password to hand them</b>
            <em>
              Shown once, on the next screen. They are still emailed a sign-in link — this is for
              when the email does not arrive, or when they are standing in front of you.
            </em>
          </span>
        </label>
      </div>

      {trouble && <p className="add-trouble">{trouble}</p>}

      <div className="add-actions">
        <button className="btn btn-primary" disabled={!ready} onClick={() => void save()}>
          {busy
            ? 'Adding…'
            : isVendor
              ? first.trim()
                ? `Add ${first.trim()} as a Vendor`
                : 'Add them as a Vendor'
              : first.trim()
                ? `Add ${first.trim()}`
                : 'Add them'}
        </button>
        <button className="btn" onClick={onDone}>
          Cancel
        </button>
        <span className="add-spacer" />
        <button className="btn btn-small add-bulk" onClick={() => setBulk(true)}>
          Adding several people at once?
        </button>
      </div>
    </div>
  );
}

/** What the pasted list amounts to, in a sentence. */
function describe(parsed: ReturnType<typeof parseAddresses>): string {
  const bits: string[] = [];
  if (parsed.ready.length) bits.push(`${parsed.ready.length} to add`);
  if (parsed.already.length) bits.push(`${parsed.already.length} already here`);
  if (parsed.wrong.length) bits.push(`${parsed.wrong.length} not an address`);
  return bits.join(' · ') || 'Nothing to add yet.';
}
